import { Injectable, Optional, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { LoggerService } from '../services/common/logger.service';

export const STATE_INITIAL_VALUE = 'STATE_INITIAL_VALUE';

/**
 * A generic state service that can be extended for specific state slices
 */
@Injectable()
export class StateService<T> {
  private state: BehaviorSubject<T>;
  private isBrowser: boolean;
  private logger: LoggerService;

  constructor(
    @Optional() @Inject(STATE_INITIAL_VALUE) initialState: T,
    @Inject(PLATFORM_ID) platformId: Object,
    logger: LoggerService,
  ) {
    this.state = new BehaviorSubject<T>(initialState || ({} as T));
    this.isBrowser = isPlatformBrowser(platformId);
    this.logger = logger;
  }

  /**
   * Get the current state value
   */
  getState(): T {
    return this.state.getValue();
  }

  /**
   * Select a specific part of the state
   * @param selector Function to select a part of the state
   */
  select<K>(selector: (state: T) => K): Observable<K> {
    return this.state
      .asObservable()
      .pipe(map(selector), distinctUntilChanged());
  }

  /**
   * Get the entire state as an observable
   */
  state$(): Observable<T> {
    return this.state.asObservable();
  }

  /**
   * Detects and formats changes between current state and next state
   * @param currentState Current state object
   * @param nextState Next state object
   * @param operation Name of the operation for context
   * @returns Formatted string with changes
   */
  private detectChanges(
    currentState: T,
    nextState: T | Partial<T>,
    operation: string,
  ): void {
    // For partial updates, we need to identify exactly what changed
    const changes: Record<string, { previous: any; current: any }> = {};

    // Find all changed fields between the two states
    const keys = new Set([
      ...Object.keys(nextState as object),
      ...Object.keys(currentState as object),
    ]);

    keys.forEach(key => {
      const k = key as keyof T;
      if (
        k in (nextState as object) &&
        nextState[k as keyof object] !== currentState[k]
      ) {
        changes[key] = {
          previous: currentState[k],
          current: nextState[k as keyof object],
        };
      }
    });

    // Only log if something actually changed
    if (Object.keys(changes).length > 0) {
      this.logger.debug(
        `[StateService] **${this.constructor.name}** ${operation} changed fields:`,
        changes,
      );
    }
  }

  /**
   * Update the state using a partial state object
   * @param statePartial Partial state to update
   */
  setState(statePartial: Partial<T>): void {
    const currentState = this.getState();
    const next = { ...currentState, ...statePartial } as T;

    if (!environment.production && this.isBrowser) {
      this.detectChanges(currentState, statePartial, 'setState()');
    }

    this.state.next(next);
  }

  /**
   * Update the state using a function that takes the current state and returns a new state
   * @param stateFn Function that takes current state and returns new state
   */
  updateState(stateFn: (state: T) => T): void {
    const currentState = this.getState();
    const next = stateFn(currentState);

    if (!environment.production && this.isBrowser) {
      this.detectChanges(currentState, next, 'updateState()');
    }

    this.state.next(next);
  }

  /**
   * Perform multiple state updates as a single batched operation
   * @param updates Array of partial state objects to be applied sequentially in one update
   */
  batchUpdates(updates: Partial<T>[]): void {
    if (updates.length === 0) return;

    const currentState = this.getState();
    let nextState = { ...currentState };

    // Apply all updates to the new state object
    for (const update of updates) {
      nextState = { ...nextState, ...update };
    }

    if (!environment.production && this.isBrowser) {
      this.detectChanges(
        currentState,
        nextState,
        `batchUpdates() with ${updates.length} updates`,
      );
    }

    this.state.next(nextState as T);
  }
}
